// invites.js — invite links that connect two accounts directly.
// Server side lives in supabase/migrations/20261011000000_invite_links.sql
// (my_invite_code, invite_preview, accept_invite). A link looks like
// https://theweb.love/?invite=abcd23efgh. Opened signed out, the code waits
// in localStorage through the first run and is accepted after sign-in.

import { supabase } from './supabase';

const PENDING_KEY = 'theweb.invite.v1';
const CODE_RE = /^[a-z0-9]{10}$/;

export const inviteUrl = (code) => `${window.location.origin}/?invite=${code}`;

export function readPendingInvite() {
  try {
    const c = localStorage.getItem(PENDING_KEY);
    return c && CODE_RE.test(c) ? c : null;
  } catch (e) {
    return null;
  }
}

export function savePendingInvite(code) {
  try { localStorage.setItem(PENDING_KEY, code); } catch (e) { /* storage unavailable */ }
}

export function clearPendingInvite() {
  try { localStorage.removeItem(PENDING_KEY); } catch (e) { /* storage unavailable */ }
}

// Moves ?invite=… from the address bar into storage, so a reload or a later
// share of the address does not carry someone else's code along.
export function captureInviteFromUrl() {
  const params = new URLSearchParams(window.location.search);
  const raw = params.get('invite');
  if (!raw) return readPendingInvite();
  const code = raw.trim().toLowerCase();
  params.delete('invite');
  const qs = params.toString();
  window.history.replaceState(null, '', `${window.location.pathname}${qs ? `?${qs}` : ''}${window.location.hash}`);
  if (!CODE_RE.test(code)) return readPendingInvite();
  savePendingInvite(code);
  return code;
}

export async function getInviteCode({ fresh = false } = {}) {
  const { data, error } = await supabase.rpc('my_invite_code', { p_new: fresh });
  if (error) throw error;
  return data;
}

// The inviter's first name, or null for an unknown code. Works signed out.
export async function previewInvite(code) {
  const { data, error } = await supabase.rpc('invite_preview', { p_code: code });
  if (error) throw error;
  return data || null;
}

// { status: 'connected' | 'already' | 'self' | 'invalid', name }
export async function acceptInvite(code) {
  const { data, error } = await supabase.rpc('accept_invite', { p_code: code });
  if (error) throw error;
  return data;
}
